// Reader-facing knobs over the ranking pipeline. Persists three numbers
// from RankParams to localStorage so they survive reload, exposes a
// React-shaped `useRankPrefs()` hook, and ships preset bundles
// ("More fresh", "More popular", "Reset") for the one-tap UX.
//
// localStorage (not IndexedDB): values are tiny and accessed synchronously
// during render. Async IDB would force a hydration step in the feed,
// which makes "instant re-rank" not actually instant on first paint.

import { useEffect, useRef, useState } from "preact/hooks";

import { DEFAULT_RANK_PARAMS, type RankParams } from "../feed/rank";

/** Storage key — matches the existing `agg:<surface>:v<int>` convention
 *  from `consent/state.ts`. Versioned so a future schema change can
 *  bump to `:v2` and migrate (or simply discard) the old payload. */
const STORAGE_KEY = "agg:rank_prefs:v1";

/** How long to wait after the last slider change before persisting.
 *  `<input type="range" onInput>` fires 30–100×/sec during a drag;
 *  without this debounce each tick triggers a synchronous
 *  `JSON.stringify + sanitize + localStorage.setItem` on the main
 *  thread, which jankily blocks the same render cycle that the
 *  user's drag is driving. 150 ms is below human-perceptible pause
 *  (the user lifts a finger then sees the value commit) and far above
 *  the busiest input-event cadence. */
const SAVE_DEBOUNCE_MS = 150;

/** Inclusive bounds enforced on every slider so a hand-edited localStorage
 *  value can't make `rankArticles` produce nonsense. */
export const RANK_PREFS_BOUNDS = {
  lambda: { min: 0, max: 2, step: 0.05 },
  recencyWeight: { min: 0, max: 1, step: 0.05 },
  recencyHalfLifeHours: { min: 1, max: 72, step: 1 },
  velocityWeight: { min: 0, max: 1, step: 0.05 },
  authorityWeight: { min: 0, max: 1, step: 0.05 },
  publisherDiversity: { min: 0, max: 1, step: 0.05 },
  topicDiversity: { min: 0, max: 1, step: 0.05 },
} as const;

/** Built-in starting points for the preset buttons in the drawer.
 *  Tuned vs. DEFAULT_RANK_PARAMS so the difference is visible after a
 *  single tap (so the user trusts that the knobs actually do something).
 *
 *  `as const satisfies Record<string, RankParams>` keeps `keyof typeof
 *  PRESETS` as the literal union of declared preset names
 *  ("default" | "fresh" | "popular" | "matched" | "explorer" today) —
 *  a typo like `applyPreset("freshh")` becomes a compile error
 *  instead of a silent runtime `undefined`. */
export const PRESETS = {
  default: { ...DEFAULT_RANK_PARAMS },
  // "Show me what's new" — heavy recency, short half-life, light everything else.
  fresh: {
    lambda: 0.3,
    recencyWeight: 0.7,
    recencyHalfLifeHours: 6,
    velocityWeight: 0.1,
    authorityWeight: 0.05,
    publisherDiversity: 0.1,
    topicDiversity: 0.1,
  },
  // "Show me what's getting traction" — lean into trending + authority.
  popular: {
    lambda: 0.1,
    recencyWeight: 0.1,
    recencyHalfLifeHours: 48,
    velocityWeight: 0.6,
    authorityWeight: 0.4,
    publisherDiversity: 0.0,
    topicDiversity: 0.0,
  },
  // "Just my topics" — heavy personalization, light recency.
  matched: {
    lambda: 1.2,
    recencyWeight: 0.15,
    recencyHalfLifeHours: 36,
    velocityWeight: 0.05,
    authorityWeight: 0.1,
    publisherDiversity: 0.0,
    topicDiversity: 0.0,
  },
  // "Broaden my horizons" — diversity-first; the radar pulls outward
  // on all six axes for a balanced silhouette.
  explorer: {
    lambda: 0.4,
    recencyWeight: 0.3,
    recencyHalfLifeHours: 24,
    velocityWeight: 0.3,
    authorityWeight: 0.2,
    publisherDiversity: 0.6,
    topicDiversity: 0.6,
  },
} as const satisfies Record<string, RankParams>;

/** The closed set of preset names. Use this in props / callback types so
 *  the drawer can't apply a preset that doesn't exist. */
export type PresetName = keyof typeof PRESETS;

/** Pick a usable number: a finite numeric value is clamped to bounds;
 *  anything else (non-number, NaN, ±Infinity) falls through to the
 *  field's default. Keeps `sanitize` short and prevents the surprising
 *  "NaN clamps to min" behavior — NaN means "broken value", not "want
 *  the smallest possible value." */
function pickNumber(
  raw: unknown,
  fallback: number,
  min: number,
  max: number,
): number {
  if (typeof raw !== "number" || !Number.isFinite(raw)) return fallback;
  return Math.min(max, Math.max(min, raw));
}

/** Coerce an arbitrary parsed JSON blob into a valid RankParams. Out-of-
 *  range numbers are clamped; missing or non-numeric values (including
 *  NaN / ±Infinity) fall back to the field's default. Pure — used by
 *  load + by the drawer's "Reset to defaults". Idempotent:
 *  `sanitize(sanitize(x))` ≡ `sanitize(x)`. */
export function sanitize(input: unknown): RankParams {
  const obj = (input ?? {}) as Partial<Record<keyof RankParams, unknown>>;
  return {
    lambda: pickNumber(
      obj.lambda,
      DEFAULT_RANK_PARAMS.lambda,
      RANK_PREFS_BOUNDS.lambda.min,
      RANK_PREFS_BOUNDS.lambda.max,
    ),
    recencyWeight: pickNumber(
      obj.recencyWeight,
      DEFAULT_RANK_PARAMS.recencyWeight,
      RANK_PREFS_BOUNDS.recencyWeight.min,
      RANK_PREFS_BOUNDS.recencyWeight.max,
    ),
    recencyHalfLifeHours: pickNumber(
      obj.recencyHalfLifeHours,
      DEFAULT_RANK_PARAMS.recencyHalfLifeHours,
      RANK_PREFS_BOUNDS.recencyHalfLifeHours.min,
      RANK_PREFS_BOUNDS.recencyHalfLifeHours.max,
    ),
    velocityWeight: pickNumber(
      obj.velocityWeight,
      DEFAULT_RANK_PARAMS.velocityWeight,
      RANK_PREFS_BOUNDS.velocityWeight.min,
      RANK_PREFS_BOUNDS.velocityWeight.max,
    ),
    authorityWeight: pickNumber(
      obj.authorityWeight,
      DEFAULT_RANK_PARAMS.authorityWeight,
      RANK_PREFS_BOUNDS.authorityWeight.min,
      RANK_PREFS_BOUNDS.authorityWeight.max,
    ),
    publisherDiversity: pickNumber(
      obj.publisherDiversity,
      DEFAULT_RANK_PARAMS.publisherDiversity,
      RANK_PREFS_BOUNDS.publisherDiversity.min,
      RANK_PREFS_BOUNDS.publisherDiversity.max,
    ),
    topicDiversity: pickNumber(
      obj.topicDiversity,
      DEFAULT_RANK_PARAMS.topicDiversity,
      RANK_PREFS_BOUNDS.topicDiversity.min,
      RANK_PREFS_BOUNDS.topicDiversity.max,
    ),
  };
}

/** Synchronous read from localStorage. Returns DEFAULT_RANK_PARAMS when
 *  storage is unavailable (private mode), empty, or unparseable —
 *  never throws. */
export function loadRankPrefs(): RankParams {
  if (typeof localStorage === "undefined") return { ...DEFAULT_RANK_PARAMS };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_RANK_PARAMS };
    return sanitize(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_RANK_PARAMS };
  }
}

/** Synchronous write. Silently no-ops when localStorage is unavailable
 *  (private mode) — the in-memory state stays correct for the session;
 *  next reload starts from defaults. */
export function saveRankPrefs(params: RankParams): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitize(params)));
  } catch {
    // Quota / disabled storage / etc. — same fallback as load.
  }
}

/** React hook: rank prefs state with debounced persistence. Re-rendering
 *  callers (Feed) get the new params synchronously, so dragging a slider
 *  re-ranks the visible list on the same frame; the disk-ish write only
 *  fires `SAVE_DEBOUNCE_MS` after the user's last change.
 *
 *  Why not persist every change: `<input type="range" onInput>` fires
 *  ~30–100×/sec during a drag, and each tick would trigger a synchronous
 *  `JSON.stringify` + `sanitize` + `localStorage.setItem` on the main
 *  thread (visible jank on low-end Android, unnecessary flash storage
 *  wear). With the debounce, the user gets instant re-rank from
 *  in-memory state and a single persist when they pause.
 *
 *  The save is best-effort and synchronous — see `saveRankPrefs`. */
export function useRankPrefs(): [RankParams, (next: RankParams) => void] {
  const [params, setParams] = useState<RankParams>(() => loadRankPrefs());
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (saveTimer.current !== null) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      saveRankPrefs(params);
    }, SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimer.current !== null) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
    };
  }, [
    params.lambda,
    params.recencyWeight,
    params.recencyHalfLifeHours,
    params.velocityWeight,
    params.authorityWeight,
    params.publisherDiversity,
    params.topicDiversity,
  ]);

  // Flush on unmount so a still-pending edit isn't lost if the user
  // navigates away within the debounce window. The cleanup above
  // cancels the pending timer; we then run one final synchronous save
  // with the latest value via a ref-snapshot pattern.
  const latest = useRef(params);
  latest.current = params;
  useEffect(() => {
    return () => {
      saveRankPrefs(latest.current);
    };
  }, []);

  return [params, setParams];
}
